/**
 * Base de données locale des Pompes À Chaleur (PAC) Air/Eau.
 *
 * Chaque entrée correspond à un modèle de PAC avec sa puissance restituée
 * à -7°C extérieur, régime de départ d'eau 55°C (zone H1, radiateurs).
 *
 * ───────────────────────────────────────────────────────────────────────
 *  ➜  POUR AJOUTER UN NOUVEAU MODÈLE :
 *      Il suffit d'ajouter une nouvelle ligne dans le tableau ci-dessous,
 *      en respectant la même structure { id, brand, model, powerAtMinus7 }.
 *      L'algorithme de filtrage/tri picke automatiquement les nouveaux
 *      modèles sans modification supplémentaire.
 *
 *      Exemple (à décommenter pour activer) :
 *      // { id: "pm24", brand: "STOVETALIA", model: "PM24 (BLN-024TC)", powerAtMinus7: 18.5 },
 *      // { id: "atlantic_xyz", brand: "Atlantic",  model: "Alfea Extensa 12", powerAtMinus7: 9.2 },
 * ───────────────────────────────────────────────────────────────────────
 */
export const PAC_DATABASE = [
  // Modèles STOVETALIA — Régime 55°C à -7°C (Zone H1)
  { id: "pm8",  brand: "STOVETALIA", model: "PM8 (BLN-008TC)",  powerAtMinus7: 6.02  },
  { id: "pm12", brand: "STOVETALIA", model: "PM12 (BLN-012TC)", powerAtMinus7: 8.93  },
  { id: "pm18", brand: "STOVETALIA", model: "PM18 (BLN-018TC)", powerAtMinus7: 13.42 },

  // ➕ Ajouter ici les futurs modèles (PM24, autres marques, etc.)
];

/** Coefficient de surpuissance utilisé pour la sélection (cohérent avec
 *  le dimensionnement recommandé de l'app : 120 %). */
export const PAC_SAFETY_FACTOR = 1.2;

/**
 * Sélectionne les PAC compatibles selon les déperditions thermiques (kW).
 *
 * @param {number} deperditionsKw - Déperditions calculées ou saisies (kW)
 * @returns {{
 *   requiredKw: number,        // Puissance minimale requise (déperditions × 1.2)
 *   matches: Array<{           // PAC compatibles, triées par puissance croissante
 *     id, brand, model, powerAtMinus7,
 *     couverturePct: number,   // (puissance / déperditions) × 100
 *     surdimensionnementPct: number  // (puissance / requiredKw - 1) × 100
 *   }>,
 *   recommended: object|null,  // Le 1er élément de matches (sinon null)
 *   isOverCapacity: boolean    // true si aucun modèle ne couvre le besoin
 * }}
 */
export function selectCompatiblePacs(deperditionsKw) {
  const requiredKw = round2(deperditionsKw * PAC_SAFETY_FACTOR);

  const enriched = PAC_DATABASE
    .filter((pac) => pac.powerAtMinus7 >= requiredKw)
    .map((pac) => ({
      ...pac,
      couverturePct: round1((pac.powerAtMinus7 / deperditionsKw) * 100),
      surdimensionnementPct: round1(((pac.powerAtMinus7 / requiredKw) - 1) * 100),
    }))
    .sort((a, b) => a.powerAtMinus7 - b.powerAtMinus7);

  return {
    requiredKw,
    matches: enriched,
    recommended: enriched[0] || null,
    isOverCapacity: enriched.length === 0,
  };
}

/** Récupère une PAC par son id. */
export function getPacById(id) {
  return PAC_DATABASE.find((p) => p.id === id) || null;
}

const round1 = (n) => Math.round(n * 10) / 10;
const round2 = (n) => Math.round(n * 100) / 100;
